import React, { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  geoRegion?: string;
  geoPlacename?: string;
  schema?: Record<string, any>;
}

export function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  geoRegion,
  geoPlacename,
  schema
}: SEOProps) {
  useEffect(() => {
    // 1. Update Document Title
    document.title = title;

    // 2. Update Meta Description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = description;
      document.head.appendChild(meta);
    }

    // 3. Update Meta Keywords
    if (keywords) {
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', keywords);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'keywords';
        meta.content = keywords;
        document.head.appendChild(meta);
      }
    }

    // 4. Update Canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    const currentUrl = canonicalUrl || window.location.href;
    if (canonical) {
      canonical.setAttribute('href', currentUrl);
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = currentUrl;
      document.head.appendChild(link);
    }

    // 5. Update Open Graph and Twitter Card tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', title);
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.setAttribute('content', description);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', currentUrl);

    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', title);
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) twitterDescription.setAttribute('content', description);

    // 6. Update Geo Meta Tags
    if (geoRegion) {
      const metaRegion = document.querySelector('meta[name="geo.region"]');
      if (metaRegion) {
        metaRegion.setAttribute('content', geoRegion);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'geo.region';
        meta.content = geoRegion;
        document.head.appendChild(meta);
      }
    }
    if (geoPlacename) {
      const metaPlacename = document.querySelector('meta[name="geo.placename"]');
      if (metaPlacename) {
        metaPlacename.setAttribute('content', geoPlacename);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'geo.placename';
        meta.content = geoPlacename;
        document.head.appendChild(meta);
      }
    }

    // 7. Inject Dynamic JSON-LD Structured Schema
    if (schema) {
      const existingSchema = document.getElementById('dynamic-page-schema');
      if (existingSchema) {
        existingSchema.innerHTML = JSON.stringify(schema);
      } else {
        const script = document.createElement('script');
        script.id = 'dynamic-page-schema';
        script.type = 'application/ld+json';
        script.innerHTML = JSON.stringify(schema);
        document.head.appendChild(script);
      }
    } else {
      const existingSchema = document.getElementById('dynamic-page-schema');
      if (existingSchema) {
        existingSchema.remove();
      }
    }

    // Cleanup: Reset dynamic elements on unmount if necessary
    return () => {
      const existingSchema = document.getElementById('dynamic-page-schema');
      if (existingSchema) {
        existingSchema.remove();
      }
    };
  }, [title, description, keywords, canonicalUrl, geoRegion, geoPlacename, schema]);

  return null;
}
